import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { Report } from "../../models/report.model";
import { ReportsService } from "../../services/reports.service";
import { ReportActions } from './report.actions';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";
import { patch } from "@ngxs/store/operators";
import { StudentEngagementListFilters } from "../../models/students-engagement-list-filters.model";
import { StudentEngagementListRequest } from "../../models/students-engagement-list-request.model";
import { AttendanceFilter } from "src/app/modules/attendance/models/attendance-filter.model";
import { StudentsAttendancesListRequest } from "../../models/students-attendances-list-request.model";

export class ReportStateModel {
  results?: Array<Report>;
  reportDetailsForm?: {
    model: Report
  };
  filters?: {
    model: Report,
    status: number
  };
  studentEngagementListFilters?: {
    model: StudentEngagementListFilters
  };
  cfg?: {
    vertical: number;
    user: string;
  };
  public isLoading: boolean;
  public error: string;
  public reportBase64File: string;
}

const defaults = {
  results: [],
  reportDetailsForm: {
    model: null,
  },
  filters: {
    model: null,
    status: -1
  },
  cfg: {
    vertical: 0,
    user: null
  },
  isLoading: false,
  error: null,
  reportBase64File: null
}

@State<ReportStateModel>({
  name: 'reports',
  defaults
})
@Injectable()
export class ReportState {
  constructor(
    private reportService: ReportsService,
    private store: Store,
    private alertService: AlertService,
    private localizationService: LocalizationService,
  ) {}

  @Action(ReportActions.GetReportsCfg)
  getReportsCfg(
    ctx: StateContext<ReportStateModel>,
    {  }: ReportActions.GetReportsCfg
  ) {
    this.reportService.getReportsCfg().subscribe(cfgServ => {
      ctx.patchState({
          cfg: cfgServ
        });
    });
  }

  @Action(ReportActions.GetReports)
  getReports(
    ctx: StateContext<ReportStateModel>,
    {vertical, description, language, status}: ReportActions.GetReports
  ) {
    ctx.patchState({isLoading: true});
    return this.reportService.getReports(vertical, description, language, status).subscribe({
      next: (res) =>
        ctx.dispatch(new ReportActions.GetReportsSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ReportActions.Error(error, ''));
      }
    });
  }

  @Action(ReportActions.GetReportsSuccess)
  getReportsSuccess(
    ctx: StateContext<ReportStateModel>,
    { result }: ReportActions.GetReportsSuccess
  ) {
    ctx.patchState({
      results: result,
      isLoading: false,
    });
  }

  @Action(ReportActions.GetReportDetails)
  getReportDetails(
    ctx: StateContext<ReportStateModel>,
    { reportId, vertical }: ReportActions.GetReportDetails
  ) {
    let state = ctx.getState();
    let report = state.results.find(report => report.reportId === reportId && report.vertical === vertical);
    ctx.patchState({ reportDetailsForm: { model: report } });
  }

  @Action(ReportActions.AddReport)
  addReport(
    ctx: StateContext<ReportStateModel>,
    {report}: ReportActions.AddReport
  ){
    this.alertService.confirm(
      this.localizationService.translate("labels.report"),
      this.localizationService.translate('masterForms.reportAddReportConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.reportAddReportConfirm')
      .replace('{0}', report.reportName).replace('{1}', report.description)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        return this.reportService.addReport(report).subscribe({
          next: (res) => {
            const newReport = {...report};
            newReport.reportId = res;
            ctx.dispatch(new ReportActions.AddReportSuccess(newReport))
          },
          error: (error) => {
            ctx.dispatch(new ReportActions.Error(error, report.reportName));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ReportActions.AddReportSuccess)
  addReportSuccess(
    ctx: StateContext<ReportStateModel>,
    {report}: ReportActions.AddReportSuccess
  ) {
    ctx.patchState({
      results: [...ctx.getState().results, report],
      isLoading: false,
    });
  }

  @Action(ReportActions.EditReport)
  editReport(
    ctx: StateContext<ReportStateModel>,
    {report}: ReportActions.EditReport
  ) {
    this.alertService.confirm(
      this.localizationService.translate("labels.report"),
      this.localizationService.translate('masterForms.reportEditReportConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.reportEditReportConfirm')
      .replace('{0}', report.reportName).replace('{1}', report.description)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        return this.reportService.updateReport(report).subscribe({
          next: (res) => ctx.dispatch(new ReportActions.EditReportSuccess(report)),
          error: (error) => {
            ctx.dispatch(new ReportActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ReportActions.EditReportSuccess)
  editReportSuccess(
    ctx: StateContext<ReportStateModel>,
    {report}: ReportActions.EditReportSuccess
  ) {
    const state = ctx.getState();
    const reports = state.results.map(object => {
      if(object.reportId === report.reportId) {
        return {...object, ...report}
      }
      return object;
    })
    ctx.patchState({
      ...state, results: reports,
      isLoading: false,
    });
  }


  @Action(ReportActions.GenerateFamilyReport)
  generateFamilyReport(
    ctx: StateContext<ReportStateModel>,
    {reportName, familyId, exerciseId}: ReportActions.GenerateFamilyReport
  ) {
    ctx.patchState({isLoading: true});
    return this.reportService.generateFamilyReport(reportName, familyId, exerciseId).subscribe({
      next: (response) =>
        ctx.dispatch(new ReportActions.GenerateFamilyReportSuccess(response)),
      error: (error) => {
        ctx.dispatch(new ReportActions.Error(error, ""));
      }
    });
  }

  @Action(ReportActions.GenerateFamilyReportSuccess)
  generateReportSuccess(
    ctx: StateContext<ReportStateModel>,
    { generatePdfBase64Response }: ReportActions.GenerateFamilyReportSuccess
  ) {
    ctx.patchState({
      isLoading: false,
      reportBase64File: generatePdfBase64Response.fileContentBase64
    });
  }

  @Action(ReportActions.GetStudentEngagementReport)
  getStudentEngagementReport(ctx: StateContext<ReportStateModel>, {}: ReportActions.GetStudentEngagementReport) {
    this.alertService.confirm(
      this.getTitle(),
      this.localizationService.translate('reports.reportGenerateConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('reports.reportGenerateConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let state = ctx.getState();
        let studentEngagementListRequest = this.getStudentEngagementListRequest(state.studentEngagementListFilters.model);
        return this.reportService.generateStudentEngagementListReport(studentEngagementListRequest).subscribe({
          next: (response) =>
            ctx.dispatch(new ReportActions.GetStudentEngagementReportSuccess(response)),
          error: (error) => {
            ctx.dispatch(new ReportActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ReportActions.GetStudentEngagementReportSuccess)
  getStudentEngagementReportSuccess(ctx: StateContext<ReportStateModel>, { generatePdfBase64Response }: ReportActions.GetStudentEngagementReportSuccess
  ) {
    ctx.patchState({
      isLoading: false,
      reportBase64File: generatePdfBase64Response.fileContentBase64
    });
  }

  @Action(ReportActions.SetStudentEngagementListCurrentExercise)
  setStudentEngagementListCurrentExercise(ctx: StateContext<ReportStateModel>, {exerciseId}: ReportActions.SetStudentEngagementListCurrentExercise) {
    ctx.setState(
      patch({
        studentEngagementListFilters: patch({
          model: patch({
            exerciseId: exerciseId,
          }),
        }),
      })
    );
  }

  @Action(ReportActions.GetStudentsAttendancesReport)
  getStudentsAttendancesReport(ctx: StateContext<ReportStateModel>, {filters, showData, orderAttendance}: ReportActions.GetStudentsAttendancesReport) {
    this.alertService.confirm(
      this.getTitle(),
      this.localizationService.translate('reports.reportGenerateConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('reports.reportGenerateConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let state = ctx.getState();
        let attendancesStudentsListRequest = this.getStudentsAttendancesListRequest(filters, showData, orderAttendance);
        return this.reportService.generateStudentsAttendancesListReport(attendancesStudentsListRequest).subscribe({
          next: (response) =>
            ctx.dispatch(new ReportActions.GetStudentsAttendancesReportSuccess(response)),
          error: (error) => {
            ctx.dispatch(new ReportActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ReportActions.GetStudentsAttendancesReportSuccess)
  getStudentsAttendancesReportSuccess(ctx: StateContext<ReportStateModel>, { generatePdfBase64Response }: ReportActions.GetStudentsAttendancesReportSuccess
  ) {
    ctx.patchState({
      isLoading: false,
      reportBase64File: generatePdfBase64Response.fileContentBase64
    });
  }

  @Action(ReportActions.Error)
  onErrorOcurred(
    ctx: StateContext<ReportStateModel>,
    {error, details}: ReportActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(ReportActions.SetDefaultState)
  setDefaultState(
    ctx: StateContext<ReportStateModel>,
    {reportDefaults}: ReportActions.SetDefaultState
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state, reportDetailsForm: {model: reportDefaults}
    });
  }

  @Action(ReportActions.SetReportDefaultState)
  setReportDefaultState(ctx: StateContext<ReportStateModel>, {reportDefaults}: ReportActions.SetDefaultState) {
    ctx.patchState(defaults);
  }

  @Action(ReportActions.SetReportAddDefaultState)
  setReportAddDefaultState(
    ctx: StateContext<ReportStateModel>,
    {}: ReportActions.SetReportAddDefaultState
  ) {
    ctx.setState(
      patch({
        reportDetailsForm: patch({
          model: defaults.reportDetailsForm.model,
        }),
      })
    );
  }

  private getStudentEngagementListRequest(studentEngagementListFilters: StudentEngagementListFilters) : StudentEngagementListRequest {
    let studentEngagementListRequest = new StudentEngagementListRequest();
    studentEngagementListRequest.exerciseId = studentEngagementListFilters.exerciseId;
    studentEngagementListRequest.zoneId = studentEngagementListFilters.zoneId;
    studentEngagementListRequest.schoolId = studentEngagementListFilters.schoolId;
    return studentEngagementListRequest;
  }

  private getStudentsAttendancesListRequest(filters: AttendanceFilter, showData: boolean, orderAttendance: number) : StudentsAttendancesListRequest {
    let studentsAttendancesListRequest = new StudentsAttendancesListRequest();
    studentsAttendancesListRequest.exerciseId = filters.exerciseId;    
    studentsAttendancesListRequest.schoolId = filters.schoolId;
    studentsAttendancesListRequest.year = filters.year;
    studentsAttendancesListRequest.month = filters.month;
    studentsAttendancesListRequest.serviceId = filters.serviceId;
    studentsAttendancesListRequest.showData = showData;
    studentsAttendancesListRequest.orderAttendance = orderAttendance; 
    return studentsAttendancesListRequest;
  }

  private getTitle() {
    return this.localizationService.translate("labels.report");
  }
}
